import React, { useState, useEffect, useCallback } from 'react'
import contentService from '../services/contentService'
import Loading from '../components/Loading'

// ── Helpers ───────────────────────────────────────────────────────────────────
const PLATAFORMA_ICON = {
  instagram: { icon: 'bi-instagram',  color: 'bg-gradient-to-br from-purple-500 to-pink-500' },
  facebook:  { icon: 'bi-facebook',   color: 'bg-blue-600' },
  tiktok:    { icon: 'bi-tiktok',     color: 'bg-gray-900' },
  youtube:   { icon: 'bi-youtube',    color: 'bg-red-600' },
  twitter:   { icon: 'bi-twitter-x',  color: 'bg-gray-800' },
  linkedin:  { icon: 'bi-linkedin',   color: 'bg-blue-700' },
}

function PlatIcon({ plataforma, size = 'w-8 h-8' }) {
  const cfg = PLATAFORMA_ICON[plataforma] || { icon: 'bi-globe', color: 'bg-gray-500' }
  return (
    <div className={`${size} rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.color}`}>
      <i className={`bi ${cfg.icon} text-white text-sm`}></i>
    </div>
  )
}

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 5000); return () => clearTimeout(t) }, [onClose])
  const colors = type === 'success'
    ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
    : 'bg-red-50 border-red-300 text-red-800'
  return (
    <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg ${colors} max-w-sm`}>
      <i className={`bi ${type === 'success' ? 'bi-check-circle-fill text-emerald-500' : 'bi-exclamation-circle-fill text-red-500'} text-lg flex-shrink-0`}></i>
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100"><i className="bi bi-x text-lg"></i></button>
    </div>
  )
}

// ── Posts Modal ───────────────────────────────────────────────────────────────
function PostsModal({ perfil, proyectoSlug, onClose }) {
  const [posts, setPosts]         = useState([])
  const [metricas, setMetricas]   = useState(null)
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState(null)
  const [dias, setDias]           = useState(30)
  const [tab, setTab]             = useState('posts')

  const cargar = useCallback(() => {
    setLoading(true); setError(null)
    const params = {
      proyecto_slug: proyectoSlug,
      profile_id:    perfil.id,
      profile_type:  perfil.profile_type,
      dias,
    }
    Promise.all([
      contentService.getSIPosts(params),
      contentService.getSIMetricas(params),
    ])
      .then(([{ data: postsData }, { data: metData }]) => {
        setPosts(postsData?.posts || postsData?.data || [])
        setMetricas(metData)
      })
      .catch(e => setError(e?.response?.data?.error || 'Error al cargar datos.'))
      .finally(() => setLoading(false))
  }, [perfil, proyectoSlug, dias])

  useEffect(() => { cargar() }, [cargar])

  const plat = perfil.profile_type?.includes('instagram') ? 'instagram'
             : perfil.profile_type?.includes('facebook')  ? 'facebook'
             : perfil.profile_type?.includes('tiktok')    ? 'tiktok'
             : perfil.profile_type?.includes('youtube')   ? 'youtube'
             : perfil.profile_type?.includes('twitter')   ? 'twitter'
             : perfil.profile_type?.includes('linkedin')  ? 'linkedin'
             : 'instagram'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <PlatIcon plataforma={plat} size="w-10 h-10" />
            <div>
              <p className="font-bold text-gray-800">{perfil.name}</p>
              <p className="text-xs text-gray-400">{perfil.profile_type} · {proyectoSlug}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={dias}
              onChange={e => setDias(Number(e.target.value))}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            >
              <option value={7}>7 días</option>
              <option value={30}>30 días</option>
              <option value={90}>90 días</option>
            </select>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <i className="bi bi-x-lg text-lg"></i>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 flex-shrink-0">
          {['posts', 'metricas'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-6 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
              {t === 'posts' ? '📄 Posts' : '📊 Métricas'}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <Loading fullScreen={false} mensaje="Cargando" />
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
              <i className="bi bi-exclamation-circle-fill mr-2"></i>{error}
            </div>
          ) : tab === 'posts' ? (
            posts.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <i className="bi bi-file-earmark-text text-4xl block mb-2"></i>
                <p className="text-sm">No hay posts en el período seleccionado</p>
              </div>
            ) : (
              <div className="space-y-3">
                {posts.slice(0, 20).map((post, i) => (
                  <div key={post.id || i} className="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:border-orange-200 transition-colors">
                    <div className="flex items-start gap-3">
                      {post.media_url && (
                        <img src={post.media_url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                          onError={e => { e.target.style.display = 'none' }} />
                      )}
                      <div className="flex-1 min-w-0">
                        {post.text && (
                          <p className="text-sm text-gray-800 line-clamp-2 mb-2">{post.text}</p>
                        )}
                        <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                          {post.sent_at && (
                            <span><i className="bi bi-calendar3 mr-1"></i>
                              {new Date(post.sent_at * 1000).toLocaleDateString('es-CL')}
                            </span>
                          )}
                          {post.statistics?.reach != null && (
                            <span><i className="bi bi-broadcast mr-1 text-orange-400"></i>{post.statistics.reach?.toLocaleString()} alcance</span>
                          )}
                          {post.statistics?.clicks != null && (
                            <span><i className="bi bi-cursor-fill mr-1 text-blue-400"></i>{post.statistics.clicks?.toLocaleString()} clics</span>
                          )}
                          {post.statistics?.likes != null && (
                            <span><i className="bi bi-heart-fill mr-1 text-red-400"></i>{post.statistics.likes?.toLocaleString()}</span>
                          )}
                          {post.statistics?.comments != null && (
                            <span><i className="bi bi-chat-fill mr-1 text-purple-400"></i>{post.statistics.comments?.toLocaleString()}</span>
                          )}
                          {post.statistics?.impressions != null && (
                            <span><i className="bi bi-eye-fill mr-1 text-green-400"></i>{post.statistics.impressions?.toLocaleString()} imp.</span>
                          )}
                        </div>
                      </div>
                      {post.service_link && (
                        <a href={post.service_link} target="_blank" rel="noopener noreferrer"
                          className="text-gray-300 hover:text-gray-500 flex-shrink-0">
                          <i className="bi bi-box-arrow-up-right"></i>
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            /* Métricas tab */
            metricas ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    ['Seguidores',    metricas.followers_count,    'bi-people-fill',      'bg-purple-500'],
                    ['Alcance',       metricas.reach,              'bi-broadcast',        'bg-orange-400'],
                    ['Impresiones',   metricas.impressions,        'bi-eye-fill',         'bg-pink-500'],
                    ['Engagement',    metricas.engagement,         'bi-hand-thumbs-up-fill','bg-blue-500'],
                    ['Posts',         metricas.posts_count,        'bi-grid-3x3-gap-fill','bg-teal-500'],
                    ['Clics',         metricas.clicks,             'bi-cursor-fill',      'bg-indigo-500'],
                  ].map(([label, val, icon, color]) => val != null && (
                    <div key={label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                        <i className={`bi ${icon} text-white text-base`}></i>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium">{label}</p>
                        <p className="text-xl font-bold text-gray-800">
                          {typeof val === 'number' ? val.toLocaleString() : val}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Datos brutos de la API</p>
                  <pre className="text-xs text-gray-600 overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(metricas, null, 2)}
                  </pre>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <i className="bi bi-bar-chart text-4xl block mb-2"></i>
                <p className="text-sm">No hay métricas disponibles para este período</p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  )
}

// ── Perfil Card ───────────────────────────────────────────────────────────────
function PerfilCard({ perfil, proyectoSlug, onVer }) {
  const plat = perfil.profile_type?.includes('instagram') ? 'instagram'
             : perfil.profile_type?.includes('facebook')  ? 'facebook'
             : perfil.profile_type?.includes('tiktok')    ? 'tiktok'
             : perfil.profile_type?.includes('youtube')   ? 'youtube'
             : perfil.profile_type?.includes('twitter')   ? 'twitter'
             : perfil.profile_type?.includes('linkedin')  ? 'linkedin'
             : 'instagram'

  const esPropia = perfil.profile_type?.includes('own')

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3 hover:border-orange-200 transition-colors">
      <PlatIcon plataforma={plat} />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-800 text-sm truncate">{perfil.name}</p>
        <p className="text-xs text-gray-400 truncate">{perfil.id}</p>
        <div className="flex items-center gap-1.5 mt-1">
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${esPropia ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
            {esPropia ? 'Cuenta propia' : 'Competencia'}
          </span>
        </div>
      </div>
      <button
        onClick={() => onVer(perfil)}
        className="flex-shrink-0 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-lg transition-colors"
      >
        Ver datos
      </button>
    </div>
  )
}

// ── Proyecto Card ─────────────────────────────────────────────────────────────
function ProyectoCard({ proyecto, onSeleccionar, activo }) {
  return (
    <button
      onClick={() => onSeleccionar(proyecto)}
      className={`w-full text-left p-4 rounded-xl border transition-all ${activo
        ? 'border-orange-400 bg-orange-50'
        : 'border-gray-100 bg-white hover:border-orange-200'}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${activo ? 'bg-orange-500' : 'bg-gray-100'}`}>
          <i className={`bi bi-folder-fill ${activo ? 'text-white' : 'text-gray-400'} text-sm`}></i>
        </div>
        <div className="min-w-0">
          <p className={`font-semibold text-sm truncate ${activo ? 'text-orange-700' : 'text-gray-800'}`}>
            {proyecto.projectname || proyecto.name || proyecto.slug}
          </p>
          <p className="text-xs text-gray-400">
            {proyecto.profiles?.length ?? 0} perfiles
          </p>
        </div>
        <i className={`bi bi-chevron-right text-xs ml-auto flex-shrink-0 ${activo ? 'text-orange-500' : 'text-gray-300'}`}></i>
      </div>
    </button>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function SocialinsiderPage() {
  const [config, setConfig]               = useState(null)
  const [apiKeyInput, setApiKeyInput]     = useState('')
  const [guardando, setGuardando]         = useState(false)
  const [proyectos, setProyectos]         = useState([])
  const [loadingProy, setLoadingProy]     = useState(false)
  const [proyectoActivo, setProyectoActivo] = useState(null)
  const [perfiles, setPerfiles]           = useState([])
  const [loadingPerf, setLoadingPerf]     = useState(false)
  const [perfilModal, setPerfilModal]     = useState(null)
  const [toast, setToast]                 = useState(null)
  const [showKeyInput, setShowKeyInput]   = useState(false)

  const showToast = (message, type = 'success') => setToast({ message, type })

  // Cargar config al montar
  const cargarConfig = useCallback(() => {
    contentService.getSIConfig()
      .then(({ data }) => {
        setConfig(data)
        if (data.configurado) cargarProyectos()
      })
      .catch(() => setConfig({ configurado: false }))
  }, [])

  useEffect(() => { cargarConfig() }, [cargarConfig])

  const cargarProyectos = () => {
    setLoadingProy(true)
    contentService.getSIProyectos()
      .then(({ data }) => setProyectos(data.projects || data || []))
      .catch(e => showToast(e?.response?.data?.error || 'Error al cargar proyectos', 'error'))
      .finally(() => setLoadingProy(false))
  }

  const handleGuardarKey = async () => {
    if (!apiKeyInput.trim()) return
    setGuardando(true)
    try {
      await contentService.setSIConfig(apiKeyInput.trim())
      showToast('API key guardada correctamente')
      setShowKeyInput(false)
      setApiKeyInput('')
      cargarConfig()
    } catch (e) {
      showToast(e?.response?.data?.error || 'Error al guardar', 'error')
    } finally { setGuardando(false) }
  }

  const handleEliminarKey = async () => {
    if (!window.confirm('¿Eliminar la API key de Socialinsider?')) return
    try {
      await contentService.deleteSIConfig()
      setConfig({ configurado: false })
      setProyectos([])
      setProyectoActivo(null)
      setPerfiles([])
      showToast('API key eliminada')
    } catch { showToast('Error al eliminar', 'error') }
  }

  const handleSeleccionarProyecto = (proyecto) => {
    setProyectoActivo(proyecto)
    setPerfiles([])
    setLoadingPerf(true)
    const slug = proyecto.projectslug || proyecto.slug || proyecto.projectname
    contentService.getSIPerfiles(slug)
      .then(({ data }) => setPerfiles(data.perfiles || []))
      .catch(e => showToast(e?.response?.data?.error || 'Error al cargar perfiles', 'error'))
      .finally(() => setLoadingPerf(false))
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6 px-4 sm:px-0 py-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {perfilModal && (
        <PostsModal
          perfil={perfilModal}
          proyectoSlug={proyectoActivo?.projectslug || proyectoActivo?.slug || proyectoActivo?.projectname}
          onClose={() => setPerfilModal(null)}
        />
      )}

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center flex-shrink-0">
            <i className="bi bi-bar-chart-fill text-white text-xl"></i>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-gray-800">Socialinsider</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Analytics unificado para Instagram, Facebook, TikTok, YouTube, LinkedIn y Twitter/X.
              Conecta vía API key para ver proyectos, perfiles y métricas.
            </p>
          </div>
          <a href="https://api-docs.socialinsider.io" target="_blank" rel="noopener noreferrer"
            className="text-xs text-orange-500 hover:text-orange-700 flex items-center gap-1">
            <i className="bi bi-book"></i>Docs API
          </a>
        </div>
      </div>

      {/* Configuración API Key */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <i className="bi bi-key-fill text-orange-500"></i>
            <p className="font-semibold text-gray-800 text-sm">API Key</p>
          </div>
          {config?.configurado && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-lg">
                <i className="bi bi-check-circle-fill mr-1"></i>Configurada {config.key_tail}
              </span>
              <button onClick={() => setShowKeyInput(v => !v)}
                className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded-lg hover:bg-gray-100">
                Cambiar
              </button>
              <button onClick={handleEliminarKey}
                className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-50">
                Eliminar
              </button>
            </div>
          )}
        </div>

        {(!config?.configurado || showKeyInput) && (
          <div className="flex gap-3">
            <input
              type="password"
              value={apiKeyInput}
              onChange={e => setApiKeyInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleGuardarKey()}
              placeholder="Pega tu Bearer token de Socialinsider aquí…"
              className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            <button
              onClick={handleGuardarKey}
              disabled={guardando || !apiKeyInput.trim()}
              className="px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50"
            >
              {guardando ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block"></span> : 'Guardar'}
            </button>
          </div>
        )}

        {!config?.configurado && !showKeyInput && (
          <p className="text-sm text-gray-400">
            Necesitas una API key de Socialinsider. Contacta a tu Account Manager en{' '}
            <a href="https://www.socialinsider.io" target="_blank" rel="noopener noreferrer"
              className="text-orange-500 hover:underline">socialinsider.io</a>.
          </p>
        )}
      </div>

      {/* Proyectos + Perfiles */}
      {config?.configurado && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Proyectos */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-gray-700">Proyectos</p>
                <button onClick={cargarProyectos} disabled={loadingProy}
                  className="text-xs text-gray-400 hover:text-gray-600">
                  <i className={`bi bi-arrow-clockwise ${loadingProy ? 'animate-spin' : ''}`}></i>
                </button>
              </div>

              {loadingProy ? (
                <Loading fullScreen={false} mensaje="Cargando" />
              ) : proyectos.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <i className="bi bi-folder text-3xl block mb-2"></i>
                  <p className="text-xs">No hay proyectos</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {proyectos.map((p, i) => (
                    <ProyectoCard
                      key={p.projectslug || p.slug || i}
                      proyecto={p}
                      activo={proyectoActivo === p}
                      onSeleccionar={handleSeleccionarProyecto}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Perfiles */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <p className="text-sm font-semibold text-gray-700 mb-3">
                {proyectoActivo
                  ? `Perfiles · ${proyectoActivo.projectname || proyectoActivo.slug}`
                  : 'Selecciona un proyecto'}
              </p>

              {!proyectoActivo ? (
                <div className="text-center py-12 text-gray-300">
                  <i className="bi bi-arrow-left-circle text-4xl block mb-2"></i>
                  <p className="text-sm text-gray-400">Elige un proyecto para ver sus perfiles</p>
                </div>
              ) : loadingPerf ? (
                <Loading fullScreen={false} mensaje="Cargando" />
              ) : perfiles.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <i className="bi bi-person text-4xl block mb-2"></i>
                  <p className="text-sm">No hay perfiles en este proyecto</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {perfiles.map((p, i) => (
                    <PerfilCard
                      key={p.id || i}
                      perfil={p}
                      proyectoSlug={proyectoActivo?.projectslug || proyectoActivo?.slug || proyectoActivo?.projectname}
                      onVer={setPerfilModal}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Info card si no está configurado */}
      {!config?.configurado && (
        <div className="bg-orange-50 border border-orange-100 rounded-2xl p-5">
          <p className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-3">
            <i className="bi bi-info-circle-fill mr-1"></i>Cómo funciona Socialinsider
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              ['Conecta vía API key', 'No requiere OAuth por plataforma. Una sola key da acceso a todos los datos.', 'bi-key-fill'],
              ['Proyectos y perfiles', 'Organiza las cuentas de tus clientes en proyectos. Cada proyecto agrupa sus redes sociales.', 'bi-folder-fill'],
              ['Analytics unificado', 'Posts, alcance, impresiones, engagement e histórico de hasta 12 meses por cuenta.', 'bi-graph-up-arrow'],
            ].map(([title, desc, icon]) => (
              <div key={title} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <i className={`bi ${icon} text-orange-500 text-sm`}></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-orange-800">{title}</p>
                  <p className="text-xs text-orange-600 mt-0.5">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
